import type { ReactNode } from 'react';
import { CarFront } from 'lucide-react';
import { formatIsoDateOnlyShort } from '@/app/utils/formatDate';
import { DetailPageHeader } from '@shared/components';
import { AgreementKeyFacts } from './AgreementKeyFacts';
import { AgreementStatusBadge } from './AgreementStatusBadge';
import type { LeftToPayDisplay } from '../utils/leftToPayDisplay';
import type { Agreement } from '../types/agreements.types';

interface Props {
    agreement: Agreement;
    availableBalance: string | null;
    leftToPay: LeftToPayDisplay;
    tabs: ReactNode;
    /** Record-level notices, shown under the facts on every tab. */
    children?: ReactNode;
}

export function AgreementHeader({ agreement, availableBalance, leftToPay, tabs, children }: Props) {
    // startDate is null until activation — render "Not started" rather
    // than an empty cell, which is also the correct rendering for a draft.
    const startDateDisplay = agreement.startDate
        ? formatIsoDateOnlyShort(agreement.startDate)
        : 'Not started';

    return (
        <DetailPageHeader
            breadcrumbs={[{ label: 'Agreements', to: '/agreements' }, { label: agreement.agreementRef }]}
            title={agreement.agreementRef}
            icon={CarFront}
            badges={<AgreementStatusBadge status={agreement.status} />}
            subtitle={
                <AgreementKeyFacts
                    startDate={startDateDisplay}
                    availableBalance={availableBalance}
                    leftToPay={leftToPay.hidden ? null : leftToPay.text}
                />
            }
            tabs={tabs}
        >
            {children}
        </DetailPageHeader>
    );
}
